import AutocompleteOrganizers from '@/components/inputs/AutocompleteOrganizers.vue';
import EventAgreementLink from '@/components/global/event-agreements/EventAgreementLink.vue';
import { dateFnsFormat } from '@/helpers/dateFns';
import { localeFormatDefault } from '@/plugins/date-print';
import DvTextField from '@/packages/ui/components/inputs/DvTextField.vue';
import DvTextarea from '@/packages/ui/components/inputs/DvTextarea.vue';
import DvSelect from '@/packages/ui/components/inputs/DvSelect.vue';
import DvCheckbox from '@/packages/ui/components/inputs/DvCheckbox.vue';
import DvPhoneField from '@/packages/ui/components/inputs/DvPhoneField.vue';
import DDatetimeField from '@/packages/ui/components/inputs/DateTimeField/DDatetimeField.vue';
import DvAutocompleteServer from '@/packages/ui/components/inputs/DvAutocompleteServer.vue';
import DvSnilsField from '@/packages/ui/components/inputs/DvSnilsField.vue';
import AddressInput from '@/components/user-redesign/inputs/AddressInput.vue';
import { rServices } from '@/plugins/api';
import { PER_PAGE } from '@/constants/pagination';

export const EVENT_STATUS = {
  ACTIVE: 1,
  DRAFT: 2,
  MODERATE: 3,
  MODERATE_REFUSE: 4,
  CLOSED: 5,
};

export const REGISTRATION_STATE_TYPE = Object.freeze({
  open: 1,
  upcoming: 2,
  unannounced: 3,
  ended: 4,
  partiallyOpen: 5,
});

export const EVENT_STATUS_CONCEALED = {
  ...EVENT_STATUS,
  TEMPLATE_SUBORDINATED: 10,
  TEMPLATE: 11,
};

export const EVENT_STATUS_TEXT = {
  [EVENT_STATUS.ACTIVE]: 'Активно',
  [EVENT_STATUS.DRAFT]: 'Черновик',
  [EVENT_STATUS.MODERATE]: 'Модерация',
  [EVENT_STATUS.MODERATE_REFUSE]: 'Отказано в модерации',
  [EVENT_STATUS.CLOSED]: 'Закрыто',
  [EVENT_STATUS_CONCEALED.TEMPLATE]: 'Шаблонное',
};

export const EVENT_STATUS_PLURAL_TEXT = {
  [EVENT_STATUS.ACTIVE]: 'Опубликованные',
  [EVENT_STATUS.DRAFT]: 'Черновики',
  [EVENT_STATUS.MODERATE]: 'На модерации',
  [EVENT_STATUS.MODERATE_REFUSE]: 'Отклонённые',
  [EVENT_STATUS.CLOSED]: 'Завершённые',
};

export const EVENT_LEVELS = {
  federal: 1,
  regional: 2,
  municipal: 3,
  local: 4,
};

export const EVENT_STAGE_FORMAT = {
  // Очный
  fulltime: 1,
  // Заочный
  parttime: 2,
  // Дистанционный
  remote: 3,
  // Очно-заочный
  fullparttime: 4,
};

export const EVENT_LEVELS_STATUS = {
  required: 1,
  recommended: 2,
  excluded: 3,
};

export const EVENT_LEVELS_STATUS_TEXT = {
  [EVENT_LEVELS_STATUS.required]: 'Обязательный',
  [EVENT_LEVELS_STATUS.recommended]: 'По желанию',
  [EVENT_LEVELS_STATUS.excluded]: 'Исключен',
};

export const EVENT_LEVELS_LIST = [
  { text: 'Локальный уровень', value: EVENT_LEVELS.local },
  { text: 'Муниципальный уровень', value: EVENT_LEVELS.municipal },
  { text: 'Региональный уровень', value: EVENT_LEVELS.regional },
  { text: 'Федеральный уровень', value: EVENT_LEVELS.federal },
];

export const EVENT_LEVELS_TEXT = {
  [EVENT_LEVELS.local]: 'Локальный уровень',
  [EVENT_LEVELS.municipal]: 'Муниципальный уровень',
  [EVENT_LEVELS.regional]: 'Региональный уровень',
  [EVENT_LEVELS.federal]: 'Федеральный уровень',
};

export const EVENT_LEVELS_STAGE_TEXT = {
  [EVENT_LEVELS.local]: 'Локальный этап',
  [EVENT_LEVELS.municipal]: 'Муниципальный этап',
  [EVENT_LEVELS.regional]: 'Региональный этап',
  [EVENT_LEVELS.federal]: 'Федеральный этап',
};

export const isStandaloneEvent = (event) => !!(event?.isMyHighest && event?.isLowest);

export const getEventLevelTitle = (event) => {
  const levelId = typeof event?.level === 'object' ? event.level?.id : event?.level;
  const titles = isStandaloneEvent(event) ? EVENT_LEVELS_TEXT : EVENT_LEVELS_STAGE_TEXT;
  return titles[levelId] || 'Мероприятие';
};

export const LEVEL_GENITIVE = {
  [EVENT_LEVELS.local]: 'локального этапа',
  [EVENT_LEVELS.municipal]: 'муниципального этапа',
  [EVENT_LEVELS.regional]: 'регионального этапа',
  [EVENT_LEVELS.federal]: 'федерального этапа',
};

export const EVENT_LEVELS_CAPTION = {
  [EVENT_LEVELS.local]: 'Локальный проводится в рамках организации',
  [EVENT_LEVELS.municipal]: 'Муниципальный для организаций, располагающихся на территории выбранного муниципалитета',
  [EVENT_LEVELS.regional]: 'Региональный для призеров муниципальных мероприятий',
  [EVENT_LEVELS.federal]: 'Федеральный для призеров региональных мероприятий',
};

export const EVENT_PRIZE_PLACE_COUNT = {
  min: 1,
  max: 100,
  none: 0,
};

export const EVENT_MAX_PARTICIPANTS = {
  min: 1,
  max: 100,
  none: null,
};

export const EVENT_DIRECTION = {
  technical: 2,
  artistic: 3,
  science: 4,
  social: 5,
  tourism: 6,
  sports: 7,
};

export const EVENT_DIRECTIONS_DATA = {
  [EVENT_DIRECTION.technical]: {
    value: EVENT_DIRECTION.technical,
    text: 'Техническая',
  },
  [EVENT_DIRECTION.artistic]: {
    value: EVENT_DIRECTION.artistic,
    text: 'Художественная',
  },
  [EVENT_DIRECTION.science]: {
    value: EVENT_DIRECTION.science,
    text: 'Естественнонаучная',
  },
  [EVENT_DIRECTION.social]: {
    value: EVENT_DIRECTION.social,
    text: 'Социально-гуманитарная',
  },
  [EVENT_DIRECTION.tourism]: {
    value: EVENT_DIRECTION.tourism,
    text: 'Туристско-краеведческая',
  },
  [EVENT_DIRECTION.sports]: {
    value: EVENT_DIRECTION.sports,
    text: 'Физкультурно-спортивная',
  },
};

export const EVENT_DIRECTIONS = [
  {
    id: null,
    name: 'Не выбрано',
  },
  ...Object.values(EVENT_DIRECTIONS_DATA).map(({ value, text }) => ({ id: value, name: text })),
];

// export const EVENT_TYPES_OBJS = [
//   {
//     value: 1,
//     text: 'Развлекательное',
//   },
//   {
//     value: 2,
//     text: 'Интеллектуальное',
//   },
//   {
//     value: 3,
//     text: 'Спортивное',
//   },
//   {
//     value: 4,
//     text: 'Игровое',
//   },
//   {
//     value: 5,
//     text: 'Театральное, творческое, музыкальное',
//   },
//   {
//     value: 6,
//     text: 'Общественное',
//   },
//   {
//     value: 7,
//     text: 'Трудовое',
//   },
// ];

export const EVENT_DIRECTION_TYPE_KEY = {
  entertainment: 1,
  intellectual: 2,
  sports: 3,
  gaming: 4,
  artistic: 5,
  social: 6,
  labor: 7, // трудовой
};

export const EVENT_DIRECTION_TYPE = {
  [EVENT_DIRECTION_TYPE_KEY.entertainment]: {
    value: EVENT_DIRECTION_TYPE_KEY.entertainment,
    text: 'Развлекательное',
  },
  [EVENT_DIRECTION_TYPE_KEY.intellectual]: {
    value: EVENT_DIRECTION_TYPE_KEY.intellectual,
    text: 'Интеллектуальное',
  },
  [EVENT_DIRECTION_TYPE_KEY.sports]: {
    value: EVENT_DIRECTION_TYPE_KEY.sports,
    text: 'Спортивное',
  },
  [EVENT_DIRECTION_TYPE_KEY.gaming]: {
    value: EVENT_DIRECTION_TYPE_KEY.gaming,
    text: 'Игровое',
  },
  [EVENT_DIRECTION_TYPE_KEY.artistic]: {
    value: EVENT_DIRECTION_TYPE_KEY.artistic,
    text: 'Театральное, творческое, музыкальное',
  },
  [EVENT_DIRECTION_TYPE_KEY.social]: {
    value: EVENT_DIRECTION_TYPE_KEY.social,
    text: 'Общественное',
  },
  [EVENT_DIRECTION_TYPE_KEY.labor]: {
    value: EVENT_DIRECTION_TYPE_KEY.labor,
    text: 'Трудовое',
  },
};

export const EVENT_DIRECTION_TYPES = Object.values(EVENT_DIRECTION_TYPE);

// For participants
export const EVENT_USER_STATUS = {
  STATUS_REQUEST: 1,
  STATUS_REJECT: 2,
  STATUS_ACTIVE: 3,
  STATUS_RESTRICTED: 4,
  STATUS_DISQUALIFIED: 5,
  STATUS_DRAFT_REQUEST: 6,
  STATUS_INCORRECT_DOCUMENTS: 7,
  STATUS_DELETED: 10,
};

export const EVENT_USER_STATUS_DATA = {
  [EVENT_USER_STATUS.STATUS_REQUEST]: {
    id: EVENT_USER_STATUS.STATUS_REQUEST,
    text: 'На рассмотрении',
    icon: '$iclock-outline',
    attrs: {
      color: 'ui-warning',
      get textColor() {
        return `${this.color}--text`;
      },
      get backgroundColor() {
        return `${this.color} lighten-4`;
      },
    },
  },
  [EVENT_USER_STATUS.STATUS_ACTIVE]: {
    id: EVENT_USER_STATUS.STATUS_ACTIVE,
    text: 'Заявка принята',
    icon: '$icheck-outline-circle',
    attrs: {
      color: 'ui-success',
      get textColor() {
        return `${this.color}--text`;
      },
      get backgroundColor() {
        return `${this.color} lighten-4`;
      },
    },
  },
  [EVENT_USER_STATUS.STATUS_REJECT]: {
    id: EVENT_USER_STATUS.STATUS_REJECT,
    text: 'Заявка отклонена',
    icon: '$ialert',
    attrs: {
      color: 'ui-error',
      get textColor() {
        return `${this.color}--text`;
      },
      get backgroundColor() {
        return `${this.color} lighten-4`;
      },
    },
  },
  // Содержит некорретные данные полей и документов
  [EVENT_USER_STATUS.STATUS_RESTRICTED]: {
    id: EVENT_USER_STATUS.STATUS_RESTRICTED,
    text: 'Заявка на заполнении',
    icon: '$ialert',
    attrs: {
      color: 'ui-error',
      get textColor() {
        return `${this.color}--text`;
      },
      get backgroundColor() {
        return `${this.color} lighten-4`;
      },
    },
  },
  [EVENT_USER_STATUS.STATUS_DISQUALIFIED]: {
    id: EVENT_USER_STATUS.STATUS_DISQUALIFIED,
    text: 'Заявка дисквалифицирована',
    icon: '$ialert',
    attrs: {
      color: 'ui-error',
      get textColor() {
        return `${this.color}--text`;
      },
      get backgroundColor() {
        return `${this.color} lighten-4`;
      },
    },
  },
  // Продолжить заполнение заявки
  [EVENT_USER_STATUS.STATUS_DRAFT_REQUEST]: {
    id: EVENT_USER_STATUS.STATUS_DRAFT_REQUEST,
    text: 'Продолжить заполнение',
    icon: 'mdi-circle-edit-outline',
    attrs: {
      color: 'ui-warning',
      get textColor() {
        return `${this.color}--text`;
      },
      get backgroundColor() {
        return `${this.color} lighten-4`;
      },
    },
  },
  // Когда организатор отправил на проверку!
  // Содержит некорректные документы
  [EVENT_USER_STATUS.STATUS_INCORRECT_DOCUMENTS]: {
    id: EVENT_USER_STATUS.STATUS_INCORRECT_DOCUMENTS,
    text: 'Исправить документы',
    icon: 'mdi-circle-edit-outline',
    attrs: {
      color: 'ui-warning',
      get textColor() {
        return `${this.color}--text`;
      },
      get backgroundColor() {
        return `${this.color} lighten-4`;
      },
    },
  },
  [EVENT_USER_STATUS.STATUS_DELETED]: {
    id: EVENT_USER_STATUS.STATUS_DELETED,
    text: 'Заявка исключена',
    icon: '$ui-close',
    attrs: {
      color: 'ui-error',
      get textColor() {
        return `${this.color}--text`;
      },
      get backgroundColor() {
        return `${this.color} lighten-4`;
      },
    },
  },
};

// For teams
export const EVENT_TEAM_STATUS = {
  STATUS_REQUEST: 1,
  STATUS_ACTIVE: 2,
  STATUS_REJECT: 3,
};

export const EVENT_TEAM_STATUS_DATA = {
  [EVENT_TEAM_STATUS.STATUS_REQUEST]: {
    id: EVENT_TEAM_STATUS.STATUS_REQUEST,
    text: 'На рассмотрении',
    icon: '$iclock-outline',
    attrs: {
      color: 'redesignWarning',
      get textColor() {
        return `${this.color}--text`;
      },
    },
  },
  [EVENT_TEAM_STATUS.STATUS_ACTIVE]: {
    id: EVENT_TEAM_STATUS.STATUS_ACTIVE,
    text: 'Заявка принята',
    icon: '$icheck-outline-circle',
    attrs: {
      color: 'redesignSuccess',
      get textColor() {
        return `${this.color}--text`;
      },
    },
  },
  [EVENT_TEAM_STATUS.STATUS_REJECT]: {
    id: EVENT_TEAM_STATUS.STATUS_REJECT,
    text: 'Заявка отменена',
    icon: '$ialert',
    attrs: {
      color: 'redesignError',
      get textColor() {
        return `${this.color}--text`;
      },
    },
  },
};

// for team requests
export const EVENT_TEAM_TRAINER_STATUS = {
  ...EVENT_USER_STATUS,
  STATUS_RECRUITMENT: 0,
};

export const EVENT_TEAM_TRAINER_STATUS_DATA = {
  ...EVENT_USER_STATUS_DATA,
  [EVENT_TEAM_TRAINER_STATUS.STATUS_RECRUITMENT]: {
    id: EVENT_TEAM_TRAINER_STATUS.STATUS_RECRUITMENT,
    text: 'Формирование команды',
    icon: '$iclock-outline',
    attrs: {
      color: 'redesignMediumGray',
      get textColor() {
        return `${this.color}--text`;
      },
    },
  },
};

// for teams requests
export const EVENT_TEAM_REQUEST_STATUS = {
  draft: 1,
  complete: 2,
};

export const EVENT_TEAM_REQUEST_STATUS_DATA = {
  [EVENT_TEAM_REQUEST_STATUS.draft]: {
    id: EVENT_TEAM_REQUEST_STATUS.draft,
    text: 'Черновик заявки',
    icon: '$iclock-outline',
    attrs: {
      color: 'redesignMediumGray',
      get textColor() {
        return `${this.color}--text`;
      },
    },
  },
  [EVENT_TEAM_REQUEST_STATUS.complete]: {
    id: EVENT_TEAM_REQUEST_STATUS.complete,
    text: 'Заявка отправлена',
    icon: '$icheck-outline-circle',
    attrs: {
      color: 'redesignSuccess',
      get textColor() {
        return `${this.color}--text`;
      },
    },
  },
};

export const IMPORT_REQUEST_STATUSES = {
  new: 1,
  in_progress: 2,
  complete: 3,
  error: 4,
};

export const IMPORT_REQUEST_DATA_STATUSES = {
  [IMPORT_REQUEST_STATUSES.new]: {
    text: 'Генерируется',
    icon: 'mdi-clock',
    attrs: {
      color: '',
      type: '',
    },
  },
  [IMPORT_REQUEST_STATUSES.in_progress]: {
    text: 'В обработке',
    icon: 'mdi-progress-clock',
    attrs: {
      color: 'warning',
      type: 'warning',
    },
  },
  [IMPORT_REQUEST_STATUSES.complete]: {
    text: 'Завершено',
    icon: 'mdi-check-circle',
    attrs: {
      color: 'success',
      type: 'success',
    },
  },
  [IMPORT_REQUEST_STATUSES.error]: {
    text: 'Ошибка',
    icon: 'mdi-alert-circle',
    attrs: {
      color: 'error',
      type: 'error',
    },
  },
};

export const SUBORDINATION_TRANSFER_STATUS = {
  new: 1,
  confirm: 2,
  reject: 3,
};

export const SUBORDINATION_TRANSFER_REQUEST_TYPE = {
  push: 1,
  pullDirect: 2,
  pullRequest: 3,
};

export const SUBORDINATION_TRANSFER_STATUS_DATA = {
  [SUBORDINATION_TRANSFER_STATUS.new]: {
    id: SUBORDINATION_TRANSFER_STATUS.new,
    userStatus: EVENT_USER_STATUS.STATUS_REQUEST,
    text: 'На рассмотрении',
    icon: '$iclock-outline',
    attrs: {
      color: 'ui-warning',
      type: '',
      get textColor() {
        return `${this.color}--text`;
      },
      get backgroundColor() {
        return `${this.color} lighten-4`;
      },
    },
  },
  [SUBORDINATION_TRANSFER_STATUS.confirm]: {
    id: SUBORDINATION_TRANSFER_STATUS.confirm,
    userStatus: EVENT_USER_STATUS.STATUS_ACTIVE,
    text: 'Передан',
    icon: '$icheck-outline-circle',
    attrs: {
      color: 'ui-success',
      type: 'success',
      get textColor() {
        return `${this.color}--text`;
      },
      get backgroundColor() {
        return `${this.color} lighten-4`;
      },
    },
  },
  [SUBORDINATION_TRANSFER_STATUS.reject]: {
    id: SUBORDINATION_TRANSFER_STATUS.reject,
    userStatus: EVENT_USER_STATUS.STATUS_REJECT,
    text: 'Отклонён',
    icon: '$ialert',
    attrs: {
      color: 'ui-error',
      type: 'error',
      get textColor() {
        return `${this.color}--text`;
      },
      get backgroundColor() {
        return `${this.color} lighten-4`;
      },
    },
  },
};

export const getSubordinationTransferStatusData = (status, isIncoming = false) => {
  const data = SUBORDINATION_TRANSFER_STATUS_DATA[status];
  if (!data || status !== SUBORDINATION_TRANSFER_STATUS.new) return data;
  return {
    ...data,
    text: isIncoming ? 'Подтвердите передачу' : 'На рассмотрении',
  };
};

export const MEMBER_JOIN_METHOD = {
  request: 1,
  imported: 2,
  subordinated: 3,
};

export const MEMBER_JOIN_METHOD_DATA = {
  [MEMBER_JOIN_METHOD.request]: {
    ...EVENT_USER_STATUS_DATA[EVENT_USER_STATUS.STATUS_ACTIVE],
    id: MEMBER_JOIN_METHOD.request,
  },
  [MEMBER_JOIN_METHOD.imported]: {
    ...EVENT_USER_STATUS_DATA[EVENT_USER_STATUS.STATUS_ACTIVE],
    id: MEMBER_JOIN_METHOD.imported,
    text: 'Импортирован',
  },
  [MEMBER_JOIN_METHOD.subordinated]: {
    ...SUBORDINATION_TRANSFER_STATUS_DATA[SUBORDINATION_TRANSFER_STATUS.confirm],
    id: MEMBER_JOIN_METHOD.subordinated,
  },
};

export const MEMBER_ENTRY_TYPE = {
  registered: 1,
  imported: 2,
};

export const MEMBER_ENTRY_TYPE_DATA = {
  [MEMBER_ENTRY_TYPE.registered]: {
    ...EVENT_USER_STATUS_DATA[EVENT_USER_STATUS.STATUS_ACTIVE],
    id: MEMBER_ENTRY_TYPE.registered,
    text: 'Самостоятельная',
  },
  [MEMBER_ENTRY_TYPE.imported]: {
    ...EVENT_USER_STATUS_DATA[EVENT_USER_STATUS.STATUS_REQUEST],
    id: MEMBER_ENTRY_TYPE.imported,
    text: 'Импортирован',
  },
};

export const USER_INPUT_TYPES = {
  address: 1,
  tel: 2,
  email: 3,
  text: 4,
  switch: 5,
  list: 6,
  organization: 7,
  file: 8,
  full_text: 9,
  agreement: 10,
  // |¯ Ниже постоянные (дефолтные), которых нет возможности кастомизировать
  birthday: 11,
  region: 12,
  municipality: 13,
  snils: 14,
  class: 15,
  // |_ Конец постоянных
};

export const USER_INPUT_TEXT_TYPES = {
  [USER_INPUT_TYPES.address]: 'Адрес',
  [USER_INPUT_TYPES.tel]: 'Телефон',
  [USER_INPUT_TYPES.email]: 'Email',
  [USER_INPUT_TYPES.text]: 'Ввод строки',
  [USER_INPUT_TYPES.full_text]: 'Ввод большого текста',
  [USER_INPUT_TYPES.switch]: 'Флажок (Согласие)',
  [USER_INPUT_TYPES.list]: 'Выбор значения из списка',
  [USER_INPUT_TYPES.file]: 'Выбор значения из списка (файл)',
  [USER_INPUT_TYPES.organization]: 'Организация',
  [USER_INPUT_TYPES.agreement]: 'Согласие',
  [USER_INPUT_TYPES.birthday]: 'Дата рождения',
  [USER_INPUT_TYPES.region]: 'Регион',
  [USER_INPUT_TYPES.municipality]: 'Муниципалитет',
  [USER_INPUT_TYPES.snils]: 'СНИЛС',
  [USER_INPUT_TYPES.class]: 'Класс',
};

export const USER_INPUT_TYPES_COMPONENTS = {
  [USER_INPUT_TYPES.address]: {
    is: AddressInput,
    rules: '',
    attrs: ({ contrast = false }) => ({
      contrast,
      placeholder: 'Введите адрес',
      'return-object': false,
      clearable: true,
    }),
  },
  [USER_INPUT_TYPES.tel]: {
    is: DvPhoneField,
    rules: 'phone',
    attrs: () => ({
      clearable: true,
    }),
  },
  [USER_INPUT_TYPES.email]: {
    is: DvTextField,
    rules: 'email|max:256',
    attrs: ({ is_required }) => ({
      type: 'email',
      placeholder: 'Введите Email',
      hint: is_required ? `*Обязательное поле` : '',
      'persistent-hint': true,
      clearable: true,
      counter: 256,
    }),
  },
  [USER_INPUT_TYPES.text]: {
    is: DvTextField,
    rules: 'max:2048',
    attrs: ({ is_required }) => ({
      placeholder: 'Введите значение',
      hint: is_required ? `*Обязательное поле` : '',
      'persistent-hint': true,
      clearable: true,
      counter: 2048,
    }),
  },
  [USER_INPUT_TYPES.full_text]: {
    is: DvTextarea,
    rules: 'max:10000',
    attrs: ({ contrast = false }) => ({
      contrast,
      outlined: true,
      placeholder: 'Введите значение',
      rows: 4,
      maxCounterValue: 10000,
      topLabel: false,
    }),
  },
  [USER_INPUT_TYPES.switch]: {
    is: DvCheckbox,
    rules: '',
    attrs: ({ is_required }) => ({
      keyValue: 'value',
      keyText: 'label',
      item: is_required,
    }),
  },
  [USER_INPUT_TYPES.list]: {
    is: DvSelect,
    rules: '',
    attrs: ({ is_many_options, is_required, options, contrast = false }) => ({
      contrast,
      clearable: true,
      hint: is_required ? `*Обязательное поле` : '',
      placeholder: 'Выберите значение из списка',
      'persistent-hint': true,
      multiple: is_many_options,
      items: options,
    }),
  },
  [USER_INPUT_TYPES.file]: {
    is: DvSelect,
    rules: '',
    attrs: ({ is_many_options, options, is_required }) => ({
      placeholder: 'Выберите значение из списка',
      hint: is_required ? `*Обязательное поле` : '',
      'persistent-hint': true,
      multiple: is_many_options,
      items: options,
    }),
  },
  [USER_INPUT_TYPES.organization]: {
    is: AutocompleteOrganizers,
    rules: '',
    attrs: () => ({
      placeholder: 'Выберите организатора',
      'background-color': 'redesignControlsBackground',
      outlined: true,
      dense: true,
      clearable: true,
      chips: false,
      'small-chips': false,
      userRegion: true,
    }),
  },
  [USER_INPUT_TYPES.agreement]: {
    is: EventAgreementLink,
    rules: '',
    attrs: ({ title, document }) => ({
      title,
      file: document,
      'hide-details': false,
    }),
  },
  [USER_INPUT_TYPES.birthday]: {
    is: DDatetimeField,
    rules: '',
    attrs: () => ({
      onlyDate: true,
      tooltip: {
        activatorClass: 'd-block full-width',
      },
    }),
  },
  [USER_INPUT_TYPES.region]: {
    is: DvAutocompleteServer,
    rules: '',
    attrs: () => ({
      method: ({ params }) => rServices.directories.getRegions({
        params: {
          ...params,
          'per-page': PER_PAGE.infinity,
        },
      }).then(({ response, error }) => ({
        items: response?.data?.data || [],
        response,
        error,
      })),
    }),
  },
  [USER_INPUT_TYPES.municipality]: {
    is: DvAutocompleteServer,
    rules: '',
    attrs: ({ region_id }) => ({
      method: ({ params }) => rServices.directories.getMunsCached({
        params: {
          ...params,
          'search[region_id]': region_id,
          'per-page': PER_PAGE.infinity,
        },
      }).then(({ response, error }) => ({
        items: response?.data?.data || [],
        response,
        error,
      })),
    }),
  },
  [USER_INPUT_TYPES.snils]: {
    is: DvSnilsField,
    rules: 'snils',
    attrs: () => ({}),
  },
  [USER_INPUT_TYPES.class]: {
    is: DvSelect,
    rules: '',
    attrs: ({ options, settings }) => ({
      clearable: true,
      placeholder: 'Выберите класс',
      items: (options?.variants || options || []).map((name) => ({ name, id: name.replace(/\D/g, '') })),
    }),
  },
};

export const TAGS_VALUES = {
  all: 1,
  important: 2,
  passed: 3,
};

export const TAGS_VALUES_TEXT = {
  [TAGS_VALUES.all]: 'Активные',
  [TAGS_VALUES.important]: 'Значимые',
  [TAGS_VALUES.passed]: 'Завершенные',
};

export const TAGS = [
  {
    text: 'Активные',
    value: TAGS_VALUES.all,
  },
  {
    text: 'Значимые',
    value: TAGS_VALUES.important,
  },
  {
    text: 'Завершенные',
    value: TAGS_VALUES.passed,
  },
];

export const EVENT_MEMBER_TYPE = {
  single: 1,
  team: 2,
  mixed: 3,
};

export const EVENT_MEMBER_TYPE_TEXT = {
  [EVENT_MEMBER_TYPE.single]: 'Индивидуальное',
  [EVENT_MEMBER_TYPE.team]: 'Командное',
  [EVENT_MEMBER_TYPE.mixed]: 'Смешанное',
};

export const EVENT_OLD_TYPE = {
  kids: 1,
  students: 2,
  adults: 3,
};

export const EVENT_OLD_TYPE_TEXT = {
  [EVENT_OLD_TYPE.kids]: 'Дети',
  [EVENT_OLD_TYPE.students]: 'Студенты',
  [EVENT_OLD_TYPE.adults]: 'Взрослые',
};

export const EVENT_OLD_TYPE_LIST = [
  { value: EVENT_OLD_TYPE.kids, text: EVENT_OLD_TYPE_TEXT[EVENT_OLD_TYPE.kids] },
  { value: EVENT_OLD_TYPE.students, text: EVENT_OLD_TYPE_TEXT[EVENT_OLD_TYPE.students] },
  { value: EVENT_OLD_TYPE.adults, text: EVENT_OLD_TYPE_TEXT[EVENT_OLD_TYPE.adults] },
];

export const NO_VALUE = {
  NAME: 'Имя отсутствует',
  AGE: 'Возраст отсутствует',
  OTHER: '-',
};

export const STAGE_CONTROL_FIELDS = {
  INDIVIDUAL_HEADERS: [
    { text: 'ФИО', value: 'name', main: true },
    { text: 'Номер Сертификата', value: 'certificate_number', details_1: true },
    {
      text: 'Возраст',
      value: 'age',
      details_1: true,
    },
  ],
  TEAMS_HEADERS: [
    {
      text: 'Наименование',
      value: 'name',
      searchable: true,
      main: true,
    },
    { text: 'Кол-во участников', value: 'active_members_count', searchable: true },
  ],
};

export const DEFAULT_NOMINATION = {
  KEY: 'default',
  NAME: 'Основная номинация',
};

export const AWARD_TYPES = {
  STAGES: 0,
  EVENT: 1,
};

export const SUBJECT_LEVEL = {
  none: 1,
  region: 2,
  municipal: 3,
  organizer: 4,
};

export const LEVEL_SUBJECT_NAMES = {
  [SUBJECT_LEVEL.none]: '',
  [SUBJECT_LEVEL.region]: 'Регион',
  [SUBJECT_LEVEL.municipal]: 'Муниципалитет',
  [SUBJECT_LEVEL.organizer]: 'Организатор',
};

export const MONITORING_REPORT_STATUS = {
  NEW: 1,
  IN_PROCESS: 2,
  COMPLETE: 3,
  ERROR: 4,
};

export const PARTICIPANTS_PER_TEAM = {
  min: 1,
  max: 25,
  default: 5,
};

export const ACCESS_VIEW_TYPE = {
  FOR_REGISTERED: 1,
  FOR_EVENT_MEMBERS: 2,
  FOR_ALL: 3,
};

export const ACCESS_VIEW_TYPE_TEXT = {
  [ACCESS_VIEW_TYPE.FOR_REGISTERED]: 'Показывать зарегистрированным',
  [ACCESS_VIEW_TYPE.FOR_EVENT_MEMBERS]: 'Показывать только участникам',
  [ACCESS_VIEW_TYPE.FOR_ALL]: 'Показывать для всех',
};

export const ACCESS_VOTE_TYPE = {
  ONLY_EVENT_MEMBERS: 1,
  ONLY_REGISTERED: 2,
  ALL: 3,
};

export const ACCESS_VOTE_TYPE_TEXT = {
  [ACCESS_VOTE_TYPE.ONLY_EVENT_MEMBERS]: 'Только участники мероприятия',
  [ACCESS_VOTE_TYPE.ONLY_REGISTERED]: 'Только зарегистрированные',
  [ACCESS_VOTE_TYPE.ALL]: 'Все',
};

export const EVAL_TYPE = {
  LIKES: 1,
  RANGE: 2,
};

export const EVAL_TYPE_TEXT = {
  [EVAL_TYPE.LIKES]: 'Лайки',
  [EVAL_TYPE.RANGE]: 'Шкала от 1 до 10',
};

export const VIEW_TYPE = {
  SHOW_EVERYTIME: 1,
  SHOW_AFTER_VOTE: 2,
  SHOW_AFTER_VOTE_END: 3,
};

export const VIEW_TYPE_TEXT = {
  [VIEW_TYPE.SHOW_EVERYTIME]: 'Показывать всегда',
  [VIEW_TYPE.SHOW_AFTER_VOTE]: 'Показывать сразу после голоса',
  [VIEW_TYPE.SHOW_AFTER_VOTE_END]: 'Показывать по окончанию срока голосования',
};

export const APPLICATION_ACTION_STATES = {
  forLowest: 1,
  forSubordinateParticipants: 2,
  forSubordinateTeams: 3,
  forLowestTeamsWithTrainer: 4,
  forExpeledList: 5,
};

export const APPLICAITON_ACTIONS_KEYS = {
  confirm: 1,
  reject: 2,
  expel: 3,
  change: 4,
  group: 5,
  removeImported: 6,
  returnExpeled: 7,
  removeExpeled: 8,
  inviteImported: 9,
};

export const EVENT_CHATS_TYPES = {
  members: 1,
  experts: 2,
  organizators: 3,
};

export const POLLS_RIGHTS = {
  UNAVAILABLE: 1,
  REQUIRED_WITH_YOURS: 2,
  REQUIRED_WITHOUT_YOURS: 3,
  RECOMMEND: 4,
};

export const POLLS_RIGHTS_TEXTS = {
  [POLLS_RIGHTS.UNAVAILABLE]: 'Недоступное задание (нельзя будет прикрепить)',
  [POLLS_RIGHTS.REQUIRED_WITH_YOURS]: 'Обязательное задание',
  [POLLS_RIGHTS.REQUIRED_WITHOUT_YOURS]: 'Обязательное задание (с запретом прикреплять свои задания, действует глобально)',
  [POLLS_RIGHTS.RECOMMEND]: 'Рекомендованное задание',
};

export const POLLS_RIGHTS_DATA = Object.values(POLLS_RIGHTS).map((right) => ({
  id: right,
  text: POLLS_RIGHTS_TEXTS[right],
}));

export const POLLS_RIGHTS_ANNOTATIONS = {
  [POLLS_RIGHTS.REQUIRED_WITHOUT_YOURS]: {
    text: '(Обязательно)',
    attrs: {
      color: 'redesignError',
      get textColor() {
        return `${this.color}--text`;
      },
    },
  },
  [POLLS_RIGHTS.REQUIRED_WITH_YOURS]: {
    text: '(Обязательно)',
    attrs: {
      color: 'redesignError',
      get textColor() {
        return `${this.color}--text`;
      },
    },
  },
  [POLLS_RIGHTS.UNAVAILABLE]: {
    text: '(Недоступно)',
    attrs: {
      color: 'redesignSecondary',
      get textColor() {
        return `${this.color}--text`;
      },
    },
  },
  [POLLS_RIGHTS.RECOMMEND]: {
    text: '(Рекомендовано)',
    attrs: {
      color: 'redesignPrimary',
      get textColor() {
        return `${this.color}--text`;
      },
    },
  },
};

export const POLLS_SETTINGS_RIGHTS = {
  REQUIRED: 1,
  RECOMMEND: 2,
};

export const POLLS_SETTINGS_RIGHTS_TEXTS = {
  [POLLS_SETTINGS_RIGHTS.REQUIRED]: 'Запретить изменение настроек задания', // обязательны
  [POLLS_SETTINGS_RIGHTS.RECOMMEND]: 'Разрешить изменение настроек задания', // рекомендованы
};

export const POLLS_SETTINGS_RIGHTS_DATA = Object.values(POLLS_SETTINGS_RIGHTS).map((right) => ({
  id: right,
  text: POLLS_SETTINGS_RIGHTS_TEXTS[right],
}));

export const APPLICATIONS_VIEW_TYPE = {
  subordination: 'subordination',
  global: 'global',
};

export const APPLICATIONS_VIEW_DATA = {
  [APPLICATIONS_VIEW_TYPE.subordination]: {
    id: APPLICATIONS_VIEW_TYPE.subordination,
    text: 'Выбор нижестоящего мероприятия',
  },
  [APPLICATIONS_VIEW_TYPE.global]: {
    id: APPLICATIONS_VIEW_TYPE.global,
    text: 'Глобальный список',
  },
};

export const GROUP_NAMES = {
  default: 'default',
  empty: 'Все категории',
  main: 'Основная категория',
};

export const EVENT_SPECIAL_PARAMS_FOR_UPDATE = [
  'presentation_url',
  'announcement',
  'target',
  'name',
  'short_name',
];

export const EVENT_SPECIAL_PARAMS_FOR_DATES = [
  'start_at',
  'end_at',
];

export const EVENT_OPTIONS_AGES = {
  max: 100,
  min: 1,
};

export const EVENT_CLONE_DATA = [
  {
    value: 'event_type',
    text: ({ event_type }) => `Тип мероприятия: ${EVENT_DIRECTION_TYPE[event_type].text}`,
    default: null,
  },
  {
    value: 'start_accepting_at',
    text: ({ start_accepting_at }) => `Начало приёма заявок: ${dateFnsFormat.readableDateWithTime(localeFormatDefault(start_accepting_at))}`,
    default: null,
  },
  {
    value: 'end_accepting_at',
    text: ({ end_accepting_at }) => `Окончание приёма заявок: ${dateFnsFormat.readableDateWithTime(localeFormatDefault(end_accepting_at))}`,
    default: null,
  },
  {
    value: 'max_participants',
    text: ({ max_participants }) => `Максимальное количество участников: ${max_participants}`,
    default: null,
  },
  {
    value: 'directory_direction_id',
    text: ({ directory_direction_id }) => `Направленность мероприятия: ${EVENT_DIRECTIONS_DATA[directory_direction_id].text}`,
    default: null,
  },
  {
    value: 'has_nominations',
    text: () => 'Программа делится на номинации',
    default: false,
  },
  {
    value: 'is_without_stages',
    text: () => 'Мероприятие проходит в один этап',
    default: false,
  },
  {
    value: 'is_limited_members',
    text: () => 'Запрет на превышение заданного количества участников',
    default: false,
  },
  {
    value: 'is_hide_members',
    text: () => 'Сокрытие данных об участниках',
    default: false,
  },
  {
    value: 'search_visible',
    text: () => 'Видимость мероприятия в поиске',
    default: true,
  },
  {
    value: 'is_without_moderation',
    text: () => 'Автоматическое принятие заявок',
    default: false,
  },
  {
    value: 'is_teamable',
    text: () => 'Разрешение создания команд',
    default: true,
  },
  {
    value: 'is_without_transfer_moderation',
    text: () => 'Автоматический перенос участников',
    default: false,
  },
  // {
  //   value: 'frontend_params',
  //   text: () => 'Стилистические параметры мероприятия',
  //   default: null,
  // },
  {
    value: 'is_olympics',
    text: () => 'Мероприятие является олимпиадой',
    default: true,
  },
  {
    value: 'directory_event_type_item_ids',
    text: () => 'Рубрики мероприятия',
    default: null,
    serverDefault: [],
  },
  {
    value: 'min_age',
    text: ({ min_age }) => `Минимальный возраст участников: ${min_age}`,
    default: EVENT_OPTIONS_AGES.min,
  },
  {
    value: 'max_age',
    text: ({ max_age }) => `Максимальный возраст участников: ${max_age}`,
    default: EVENT_OPTIONS_AGES.max,
  },
];
